import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import toast from 'react-hot-toast';
import api from '../services/api.js';
import SSOButtons, { SSO_ENABLED } from '../components/SSOButtons.jsx';

// ── Left panel illustration ───────────────────────────────────────────────────
// Shows a live-ish team roster card + activity feed previewing the app's UI.
// Uses hardcoded colours that work on both light and dark panel backgrounds.
function PanelArt() {
  const blue   = '#1d4ed8';
  const green  = '#059669';
  const orange = '#ea580c';
  const slate  = '#334155';
  const muted  = '#94a3b8';
  const white  = '#ffffff';
  const card   = 'rgba(255,255,255,0.92)';
  const cardBorder = 'rgba(15,23,42,0.10)';

  const players = [
    { name: 'Jordan M.',  pos: 'FWD', rating: 1, color: blue   },
    { name: 'Taylor K.',  pos: 'DEF', rating: 2, color: blue   },
    { name: 'Morgan R.',  pos: 'FWD', rating: 1, color: green  },
    { name: 'Casey L.',   pos: 'GK',  rating: 2, color: orange },
    { name: 'Riley S.',   pos: 'FWD', rating: 3, color: blue   },
  ];

  const feed = [
    { icon: '✅', text: 'Jordan is IN for Friday', time: '2m' },
    { icon: '📅', text: 'New game added — Apr 11',  time: '1h' },
    { icon: '⚖️',  text: 'Teams balanced (92/100)', time: '3h' },
  ];

  return (
    <svg viewBox="0 0 380 520" xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}>
      <defs>
        <filter id="reg-shadow" x="-10%" y="-10%" width="120%" height="130%">
          <feDropShadow dx="0" dy="2" stdDeviation="6" floodColor="rgba(15,23,42,0.10)" />
        </filter>
        <filter id="reg-shadow-sm" x="-5%" y="-5%" width="115%" height="125%">
          <feDropShadow dx="0" dy="1" stdDeviation="3" floodColor="rgba(15,23,42,0.08)" />
        </filter>
      </defs>

      {/* ── Background field pattern ── */}
      {/* Faint hexagonal dots */}
      {[...Array(6)].map((_, row) =>
        [...Array(5)].map((_, col) => {
          const x = col * 75 + (row % 2 === 0 ? 0 : 37);
          const y = row * 62 - 20;
          return (
            <circle key={`${row}-${col}`} cx={x} cy={y} r="2.5"
              fill={blue} opacity="0.08" />
          );
        })
      )}

      {/* ── Roster card ── */}
      <rect x="20" y="18" width="340" height="220" rx="16"
        fill={card} stroke={cardBorder} strokeWidth="1" filter="url(#reg-shadow)" />

      {/* Card header */}
      <rect x="20" y="18" width="340" height="48" rx="16" fill={blue} />
      <rect x="20" y="50" width="340" height="16" fill={blue} />
      <text x="36" y="48" fill={white} fontSize="12" fontWeight="700"
        fontFamily="system-ui,sans-serif" letterSpacing="0.04em">TEAM ROSTER</text>
      <text x="36" y="64" fill="rgba(255,255,255,0.7)" fontSize="10"
        fontFamily="system-ui,sans-serif">5 players · Game Friday 8:00 PM</text>

      {/* Player rows */}
      {players.map((p, i) => {
        const y = 82 + i * 32;
        const stars = p.rating === 1 ? '★★★' : p.rating === 2 ? '★★☆' : '★☆☆';
        return (
          <g key={i}>
            {/* Avatar circle */}
            <circle cx="44" cy={y + 10} r="10" fill={p.color} opacity="0.15" />
            <text x="44" y={y + 14} textAnchor="middle" fontSize="11"
              fill={p.color} fontWeight="700" fontFamily="system-ui,sans-serif">
              {p.name[0]}
            </text>
            {/* Name */}
            <text x="62" y={y + 9} fontSize="11" fill={slate} fontWeight="600"
              fontFamily="system-ui,sans-serif">{p.name}</text>
            {/* Position badge */}
            <rect x="62" y={y + 13} width="26" height="12" rx="4" fill={p.color} opacity="0.12" />
            <text x="75" y={y + 22} textAnchor="middle" fontSize="8" fill={p.color}
              fontWeight="700" fontFamily="system-ui,sans-serif">{p.pos}</text>
            {/* Rating */}
            <text x="310" y={y + 14} textAnchor="end" fontSize="10" fill="#f59e0b"
              fontFamily="system-ui,sans-serif">{stars}</text>
            {/* Status dot */}
            <circle cx="325" cy={y + 10} r="5"
              fill={i < 3 ? green : i === 3 ? orange : '#e2e8f0'} />
          </g>
        );
      })}

      {/* ── Balance score card ── */}
      <rect x="20" y="254" width="160" height="82" rx="14"
        fill={card} stroke={cardBorder} strokeWidth="1" filter="url(#reg-shadow-sm)" />
      <text x="36" y="278" fontSize="10" fill={muted} fontWeight="600"
        fontFamily="system-ui,sans-serif" letterSpacing="0.06em">BALANCE SCORE</text>
      <text x="36" y="308" fontSize="34" fill={blue} fontWeight="800"
        fontFamily="system-ui,sans-serif">92</text>
      <text x="78" y="308" fontSize="16" fill={muted}
        fontFamily="system-ui,sans-serif">/100</text>
      {/* Mini progress bar */}
      <rect x="36" y="318" width="120" height="6" rx="3" fill="rgba(29,78,216,0.10)" />
      <rect x="36" y="318" width="110" height="6" rx="3" fill={blue} opacity="0.7" />

      {/* ── Next game card ── */}
      <rect x="196" y="254" width="164" height="82" rx="14"
        fill={card} stroke={cardBorder} strokeWidth="1" filter="url(#reg-shadow-sm)" />
      <text x="212" y="278" fontSize="10" fill={muted} fontWeight="600"
        fontFamily="system-ui,sans-serif" letterSpacing="0.06em">NEXT GAME</text>
      <text x="212" y="300" fontSize="14" fill={slate} fontWeight="700"
        fontFamily="system-ui,sans-serif">Friday, Apr 11</text>
      <text x="212" y="316" fontSize="11" fill={muted}
        fontFamily="system-ui,sans-serif">8:00 PM · Civic Arena</text>
      {/* RSVP bar */}
      <rect x="212" y="322" width="132" height="6" rx="3" fill="rgba(5,150,105,0.12)" />
      <rect x="212" y="322" width="92"  height="6" rx="3" fill={green} opacity="0.7" />
      <text x="352" y="328" textAnchor="end" fontSize="9" fill={green}
        fontFamily="system-ui,sans-serif" fontWeight="600">12 IN</text>

      {/* ── Activity feed card ── */}
      <rect x="20" y="352" width="340" height="122" rx="14"
        fill={card} stroke={cardBorder} strokeWidth="1" filter="url(#reg-shadow-sm)" />
      <text x="36" y="376" fontSize="10" fill={muted} fontWeight="600"
        fontFamily="system-ui,sans-serif" letterSpacing="0.06em">RECENT ACTIVITY</text>
      {feed.map((item, i) => {
        const y = 394 + i * 28;
        return (
          <g key={i}>
            <text x="36"  y={y} fontSize="13" fontFamily="system-ui,sans-serif">{item.icon}</text>
            <text x="56"  y={y} fontSize="11" fill={slate}
              fontFamily="system-ui,sans-serif">{item.text}</text>
            <text x="352" y={y} textAnchor="end" fontSize="10" fill={muted}
              fontFamily="system-ui,sans-serif">{item.time}</text>
            {i < feed.length - 1 && (
              <line x1="36" y1={y + 8} x2="354" y2={y + 8}
                stroke="rgba(15,23,42,0.05)" strokeWidth="1" />
            )}
          </g>
        );
      })}

      {/* ── Bottom stats row ── */}
      {[
        { label: 'Teams balanced', value: '47' },
        { label: 'Games played',   value: '128' },
        { label: 'Happy players',  value: '94%' },
      ].map((s, i) => (
        <g key={i}>
          <text x={46 + i * 116} y="498" textAnchor="middle" fontSize="18"
            fill={blue} fontWeight="800" fontFamily="system-ui,sans-serif">{s.value}</text>
          <text x={46 + i * 116} y="512" textAnchor="middle" fontSize="9"
            fill={muted} fontFamily="system-ui,sans-serif">{s.label}</text>
        </g>
      ))}
    </svg>
  );
}

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate     = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm]       = useState({ name: '', email: '', password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  // v2.5.180 — invite email prefill + mismatch guard.
  // invitedEmail: the address the coach sent the invite to (from the
  // public preview). We prefill it so the joiner doesn't retype it and
  // so their account matches the invite. If they change it, we ask them
  // to confirm once (the address could be a typo or a wrong account),
  // but we don't force it — a player may legitimately prefer a different
  // email than the one the coach happened to have.
  const inviteToken = searchParams.get('invite') || '';
  const [invitedEmail, setInvitedEmail] = useState('');
  const [pendingMismatch, setPendingMismatch] = useState(false);

  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    (async () => {
      try {
        // Public endpoint — skipAuth so a stale token can't derail it
        // (same hardening as InviteAcceptPage, v2.5.176).
        const { data } = await api.get(`/invite-preview/${inviteToken}`, { skipAuth: true });
        if (cancelled) return;
        if (data?.invitedEmail) {
          setInvitedEmail(data.invitedEmail);
          // Only prefill if the user hasn't already typed something.
          setForm(p => (p.email ? p : { ...p, email: data.invitedEmail }));
        }
      } catch {
        // Preview failure is non-fatal here — the invite page handles
        // surfacing real invite problems; registration can still proceed.
      }
    })();
    return () => { cancelled = true; };
  }, [inviteToken]);

  // Normalize for comparison (case/whitespace-insensitive).
  const norm = (s) => String(s || '').trim().toLowerCase();
  const emailDiffersFromInvite = !!invitedEmail
    && norm(form.email).length > 0
    && norm(form.email) !== norm(invitedEmail);

  // Mask an email for the confirmation prompt: j•••@gmail.com
  const maskEmail = (em) => {
    const [u, d] = String(em).split('@');
    if (!d) return em;
    const head = u.slice(0, 1);
    return `${head}${'•'.repeat(Math.max(2, u.length - 1))}@${d}`;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.password !== form.confirm) { toast.error('Passwords do not match'); return; }
    // v2.5.180 — if the joiner changed the email away from the one the
    // coach invited, pause and ask them to confirm once. Catches typos
    // and wrong-account mistakes (which would otherwise create an
    // account the coach's invite can't match), while still letting them
    // proceed deliberately. Once confirmed, pendingMismatch stays true
    // so we don't re-prompt.
    if (emailDiffersFromInvite && !pendingMismatch) {
      setPendingMismatch(true);
      return;
    }
    // Password rule — keep in sync with backend /auth/register express-validator
    // chain. 10–72 chars + uppercase + lowercase + number + special char.
    if (form.password.length < 10) { toast.error('Password must be at least 10 characters'); return; }
    if (form.password.length > 72) { toast.error('Password must be 72 characters or fewer'); return; }
    if (!/[A-Z]/.test(form.password)) { toast.error('Password must contain at least one uppercase letter'); return; }
    if (!/[a-z]/.test(form.password)) { toast.error('Password must contain at least one lowercase letter'); return; }
    if (!/[0-9]/.test(form.password)) { toast.error('Password must contain at least one number'); return; }
    if (!/[^A-Za-z0-9]/.test(form.password)) { toast.error('Password must contain at least one special character (e.g. !@#$%)'); return; }
    setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      toast.success('Account created! Check your email to verify.');
      navigate('/dashboard');
    } catch (err) {
      // isInviteFailure = account was created and user is logged in,
      // but the invite couldn't be applied. Show the invite-specific
      // message and send them to dashboard (they're signed up).
      if (err.isInviteFailure) {
        toast.success('Account created!');
        toast.error(err.message, { duration: 6000 });
        navigate('/dashboard');
      } else {
        const data = err.response?.data;
        const msg = data?.errors?.[0]?.msg || data?.error || 'Registration failed';
        // v2.5.174 — graceful "you already have an account" recovery.
        // Common case: an already-registered player gets a NEW invite
        // link by accident, taps the prominent "Create Account" button,
        // and submits with their existing email. The backend returns
        // 409 "Email already registered". Instead of dead-ending on a
        // toast, send them to Sign In with the invite token preserved
        // (URL ?invite= takes priority; otherwise the pendingInviteToken
        // that InviteAcceptPage stashed), so signing in still auto-
        // accepts the invite. Without this they'd be stuck not knowing
        // their email already has an account.
        const isDuplicateEmail = err.response?.status === 409
          && /already registered/i.test(String(data?.error || ''));
        if (isDuplicateEmail) {
          const inviteToken = searchParams.get('invite')
            || localStorage.getItem('pendingInviteToken')
            || '';
          // Make sure the login flow can auto-accept: AuthContext.login
          // reads pendingInviteToken from localStorage (not the URL), so
          // persist it here in case the user arrived via a direct
          // /register?invite=… link that never passed through
          // InviteAcceptPage (which is what normally sets it).
          if (inviteToken) localStorage.setItem('pendingInviteToken', inviteToken);
          toast.error('You already have an account with this email — please sign in.', { duration: 6000 });
          navigate(inviteToken ? `/login?invite=${inviteToken}` : '/login', {
            // Prefill the email so they don't have to retype it.
            state: { email: form.email.trim(), fromDuplicateRegister: true },
          });
        } else {
          toast.error(msg);
        }
      }
    }
    setLoading(false);
  };

  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg)' }}>

      {/* ── Left panel ── */}
      <div className="hidden lg:flex flex-col justify-between p-10 relative overflow-hidden"
        style={{
          width: 420, flexShrink: 0,
          background: 'linear-gradient(160deg, #0f2557 0%, #1e3a8a 50%, #1e4d6b 100%)',
          borderRight: '1px solid var(--border)',
        }}>

        {/* Subtle grain overlay */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.03,
          backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
        }} />

        {/* App preview illustration */}
        <div style={{ position: 'absolute', inset: 0, padding: '140px 20px 130px' }}>
          <PanelArt />
        </div>

        {/* Brand */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 48,
            letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: 0.9,
            color: '#ffffff',
          }}>GAME<br/>DAY</div>
          <div style={{
            marginTop: 10, fontSize: 10, letterSpacing: '0.28em',
            color: 'var(--text-secondary)', textTransform: 'uppercase',
            fontFamily: 'var(--font-display)',
          }}>TEAM MANAGER</div>
        </div>

        {/* Bottom tagline */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            fontFamily: 'var(--font-display)', fontWeight: 800, fontStyle: 'italic',
            fontSize: 30, lineHeight: 1.1, color: '#ffffff', marginBottom: 18, opacity: 0.95,
          }}>
            "YOUR<br/>TEAM.<br/>YOUR GAME."
          </div>
          <div style={{ height: 1, background: 'var(--border)', marginBottom: 14 }} />
          {[
            { icon: '✅', text: 'Smart team balancing' },
            { icon: '📅', text: 'Automated scheduling' },
            { icon: '🤖', text: 'AI-powered insights' },
            { icon: '💬', text: 'Real-time team chat' },
          ].map((f, i) => (
            <div key={i} style={{
              fontSize: 12, color: 'var(--text-secondary)', marginBottom: 7,
              letterSpacing: '0.02em', display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 14 }}>{f.icon}</span> {f.text}
            </div>
          ))}
        </div>
      </div>

      {/* ── Right: form ── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 animate-fade-in">

        {/* Mobile brand */}
        <div className="lg:hidden mb-10 text-center">
          <div style={{
            fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 36,
            letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text)',
          }}>🏟 Game Day</div>
        </div>

        <div style={{ width: '100%', maxWidth: 380 }}>
          <div className="mb-8">
            <h1 style={{
              fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 32,
              letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text)',
              lineHeight: 1, marginBottom: 8,
            }}>Get Started</h1>
            <p style={{ color: 'var(--text-2)', fontSize: 14 }}>
              Create your free account in seconds
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Full Name</label>
              <input type="text" className="input-field" placeholder="Alex Smith"
                value={form.name} onChange={set('name')} required autoComplete="name" />
            </div>
            <div>
              <label className="label">Email</label>
              <input type="email" className="input-field" placeholder="you@email.com"
                value={form.email}
                onChange={(e) => {
                  // Editing the email invalidates a prior mismatch
                  // confirmation, so a fresh change re-triggers the check.
                  setPendingMismatch(false);
                  setForm(p => ({ ...p, email: e.target.value }));
                }}
                required autoComplete="email" />
              {invitedEmail && !emailDiffersFromInvite && (
                <p style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 6 }}>
                  Invited as <strong style={{ color: 'var(--text-1)' }}>{invitedEmail}</strong>
                </p>
              )}
            </div>
            <div>
              <label className="label">Password</label>
              <input type="password" className="input-field"
                placeholder="At least 10 characters"
                value={form.password} onChange={set('password')} required autoComplete="new-password" />
              {/* Live requirement checklist — shows the user each rule and
                  ticks it as they type. We always render it on the register
                  page (even before they type anything) so first-time users
                  know what's expected BEFORE choosing a password, not after
                  they've been rejected. Order matches perceived difficulty:
                  length first, then the four character classes. ── */}
              {(() => {
                const p = form.password || '';
                const checks = [
                  { ok: p.length >= 10,         label: 'At least 10 characters' },
                  { ok: /[A-Z]/.test(p),        label: 'One uppercase letter (A–Z)' },
                  { ok: /[a-z]/.test(p),        label: 'One lowercase letter (a–z)' },
                  { ok: /[0-9]/.test(p),        label: 'One number (0–9)' },
                  { ok: /[^A-Za-z0-9]/.test(p), label: 'One special character (e.g. !@#$%)' },
                ];
                return (
                  <ul style={{ marginTop: 8, paddingLeft: 0, listStyle: 'none' }}>
                    {checks.map(c => (
                      <li key={c.label}
                        style={{
                          fontSize: 12,
                          lineHeight: 1.6,
                          color: c.ok ? 'var(--goal)' : 'var(--text-2)',
                          display: 'flex', alignItems: 'center', gap: 6,
                        }}>
                        <span style={{
                          display: 'inline-block',
                          width: 14, textAlign: 'center',
                          color: c.ok ? 'var(--goal)' : 'var(--text-3)',
                          fontWeight: 700,
                        }}>
                          {c.ok ? '✓' : '○'}
                        </span>
                        {c.label}
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
            <div>
              <label className="label">Confirm Password</label>
              <input type="password" className="input-field" placeholder="••••••••"
                value={form.confirm} onChange={set('confirm')} required autoComplete="new-password" />
            </div>

            {emailDiffersFromInvite && (
              <div style={{
                padding: '12px 14px', borderRadius: 10,
                background: 'var(--puck-a8, rgba(251,146,60,0.08))',
                border: '1px solid var(--puck-a25, rgba(251,146,60,0.3))',
                fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.55,
              }}>
                <strong style={{ color: 'var(--puck, #fb923c)' }}>Heads up:</strong>{' '}
                this is a different email than the one your coach invited
                {invitedEmail ? <> (<strong style={{ color: 'var(--text-1)' }}>{maskEmail(invitedEmail)}</strong>)</> : null}.
                You can use it, but your coach may not recognize the new address.
                <button
                  type="button"
                  onClick={() => {
                    setForm(p => ({ ...p, email: invitedEmail }));
                    setPendingMismatch(false);
                  }}
                  style={{
                    display: 'block', marginTop: 8, background: 'none', border: 'none',
                    padding: 0, color: 'var(--ice)', fontSize: 12.5, fontWeight: 600,
                    cursor: 'pointer', textDecoration: 'underline',
                  }}>
                  Use the invited email instead
                </button>
              </div>
            )}

            <button type="submit" disabled={loading} className="btn-goal w-full justify-center mt-2"
              style={{ padding: '13px 20px', fontSize: 14, letterSpacing: '0.1em' }}>
              {loading
                ? <span className="spinner" style={{ width: 18, height: 18 }} />
                : (emailDiffersFromInvite && pendingMismatch
                    ? 'CONFIRM & CREATE ACCOUNT →'
                    : 'CREATE ACCOUNT →')}
            </button>
          </form>

          {SSO_ENABLED && (<>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <span style={{ fontSize: 11, color: 'var(--text-2)', letterSpacing: '0.1em', fontFamily: 'var(--font-display)' }}>OR</span>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>

          <SSOButtons
            onSuccess={() => navigate('/dashboard')}
            onError={(msg) => toast.error(msg)}
          />
          </>)}

          <div className="ice-divider my-6" />

          <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-2)' }}>
            Already have an account?{' '}
            <Link to="/login" style={{
              color: 'var(--ice)', fontWeight: 700,
              fontFamily: 'var(--font-display)', letterSpacing: '0.05em',
            }}>SIGN IN</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
